import { createFileRoute } from '@tanstack/react-router'

import type { User } from 'oidc-client-ts'
import { z } from 'zod'

import type { RouterContext } from '../../App'

// Same-origin path-absolute only: one leading slash, not "//" or "/\" (both
// normalise to a cross-origin URL), no scheme/whitespace/backslash. Closes the
// open-redirect + javascript:-URI DOM-XSS at the location.href sink.
const SAME_ORIGIN_PATH = /^\/(?![/\\])[^\s\\]*$/

export const getRedirectUrl = (
  user: User | null | undefined,
): string | null => {
  if (typeof user?.state !== 'string') return null

  const stateSchema = z.object({
    redirect_to: z.string().regex(SAME_ORIGIN_PATH),
  })

  try {
    const parsed = stateSchema.parse(JSON.parse(user.state))
    return parsed.redirect_to
  } catch {
    return null
  }
}

type BeforeLoadParams = {
  context: RouterContext
  search: { redirect_to?: string }
}

export const beforeLoad = async ({ context, search }: BeforeLoadParams) => {
  const { auth } = context
  const { redirect_to = '/' } = search

  if (auth?.isAuthenticated) {
    // After successful authentication, redirect to the original URL
    const redirectUrlFromState = getRedirectUrl(auth.user)

    if (redirectUrlFromState) {
      window.location.href = redirectUrlFromState
    } else {
      window.location.pathname = '/'
    }
  } else {
    // Pass the redirect URL through the OIDC state parameter
    const state = JSON.stringify({ redirect_to })
    await auth?.signinRedirect({ state })
  }
}

export const Route = createFileRoute('/_app/callback')({
  validateSearch: z.object({
    // Invalid/hostile values degrade to undefined (beforeLoad then defaults to
    // '/') instead of throwing the error component during the login flow.
    redirect_to: z.string().regex(SAME_ORIGIN_PATH).optional().catch(undefined),
  }),
  beforeLoad,
})
